/*  UI  */
import { Button, Flex, IconWithVariant } from '@auviknetworks/iceberg-ui';
import { Dropdown } from 'application/Shared/Dropdown';
import { type Item } from 'application/Shared/Dropdown/Menu';

/*  Packages  */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useDashboardContext } from '../DashboardContext';

interface TagOption {
    /** The label of the tag. */
    label: string;
    /** The value of the tag. */
    value: string | null;
}

interface FiltersProps {
    /** The tags to filter by. */
    tags?: string[];
    /** The selected tag. */
    selected: string | null;
    /** The function to call when a tag is selected. */
    onSelect: (tag: string | null) => void;
    /** The current breakpoint. */
    currentBreakpoint: string;
}

/**
 * Filters buttons to select a tag.
 * Displays a button for each tag, and a dropdown for overflow tags.
 */
export const Filters = ({
    tags,
    selected,
    onSelect,
    currentBreakpoint
}: FiltersProps) => {
    const { t } = useTranslation();
    const {
        dashboardState: { isEditing }
    } = useDashboardContext();

    // If there is only one tag, don't show the filters.
    if (!tags || tags.length <= 1) return null;

    // Add an "All" option to the tags.
    const allOptions: TagOption[] = [
        { label: 'All', value: null },
        ...tags
            .map((tag) => ({ label: tag, value: tag }))
            // Sort the tags alphabetically
            .sort((a, b) => a.label.localeCompare(b.label))
    ];

    // Helper to build menu items for dropdown
    const buildMenuItems = (tagList: TagOption[]): Item[] => [
        ...tagList.map((option) => ({
            action: () => onSelect(option.value),
            disabled: isEditing || selected === option.value,
            labelKey: option.label,
            type: 'leaf' as const
        }))
    ];

    // Responsive logic
    // If the current breakpoint is xxs, show all tags in the dropdown.
    // If the current breakpoint is xs, show 1 tag and the rest in the dropdown.
    // If the current breakpoint is sm, show 3 tags and the rest in the dropdown.
    // If the current breakpoint is md or up, show 5 tags and the rest in the dropdown.
    let visibleTags: TagOption[] = [];
    let dropdownTags: TagOption[] = [];
    if (currentBreakpoint === 'xxs') {
        dropdownTags = allOptions;
    } else if (currentBreakpoint === 'xs') {
        visibleTags = allOptions;
        visibleTags = allOptions.slice(0, 1);
        dropdownTags = allOptions.slice(1);
    } else if (currentBreakpoint === 'sm') {
        visibleTags = allOptions.slice(0, 3);
        dropdownTags = allOptions.slice(3);
    } else {
        // md and up
        visibleTags = allOptions.slice(0, 5);
        dropdownTags = allOptions.slice(5);
    }

    return (
        <Flex alignItems="center" height="32px" sx={{ gap: '8px' }}>
            {/* Visible tag buttons */}
            {visibleTags.map((option) => (
                <Button
                    key={option.value}
                    variant={
                        selected === option.value ? 'primary' : 'secondary'
                    }
                    size="small"
                    onClick={() => onSelect(option.value)}
                    disabled={isEditing}
                >
                    {option.label}
                </Button>
            ))}
            {/* Dropdown for overflow tags or only dropdown for xxs */}
            {dropdownTags.length > 0 && (
                <Dropdown
                    menu={buildMenuItems(dropdownTags)}
                    // Use a filter icon as the dropdown button
                >
                    <Button
                        variant="icon"
                        size="icon"
                        disabled={isEditing}
                        title={t('Dashboard.filters')}
                        textStyle="greyIcon"
                        // Icon button is slightly bigger than the other buttons, so we need to set the height and width to 32px
                        sx={{ height: '32px', width: '32px' }}
                    >
                        <IconWithVariant src="HorizontalSlider" size="16px" />
                    </Button>
                </Dropdown>
            )}
        </Flex>
    );
};
